/**
 * Command handling context and the permission checks of tier 1 (docs/design/02 §4.1).
 *
 * The server is authoritative for integrity and privacy even when it enforces no rules:
 * - a command may only name objects whose id the actor can see; hidden and missing objects are both
 *   `unknownObject`, so probing ids reveals nothing;
 * - the actor may only act on objects they **control** (board zones, the chain) or **own** (hand, decks,
 *   trash, banishment, champion zone), never on another player's (02 §8: "can't touch opponent objects").
 */
import { type Rejection, reject } from '../../kernel/errors';
import type { TableEvent } from '../events/types';
import type { GameState } from '../state/game-state';
import type { PlayerId } from '../state/ids';
import type { GameObject } from '../state/objects';
import { isBoardZone } from '../state/zones';
import { findObject } from '../core/queries';
import { canSeeId } from '../visibility/visibility';

/** What a command handler works on. */
export interface CommandContext {
  /** The working copy; discarded if the handler rejects. */
  readonly draft: GameState;
  readonly actor: PlayerId;
  readonly events: TableEvent[];
  /**
   * Set by handlers whose effect can't be taken back fairly: it used randomness or showed the actor
   * hidden cards (draw, shuffle, look at, reveal…). It clears the undo history (TR 509.7: no fishing).
   */
  barrier: boolean;
}

/** A context over a fresh draft. */
export function createContext(draft: GameState, actor: PlayerId): CommandContext {
  return { draft, actor, events: [], barrier: false };
}

/** Records an event of the step. */
export function emit(ctx: CommandContext, event: TableEvent): void {
  ctx.events.push(event);
}

/** Whether `actor` may act on `object`: control on the board and chain, ownership elsewhere. */
export function mayActOn(object: GameObject, actor: PlayerId): boolean {
  return isBoardZone(object.zone) || object.zone.kind === 'chain'
    ? object.controller === actor
    : object.owner === actor;
}

/** The object, if its id is visible to the actor; otherwise `unknownObject` (missing and hidden look the same). */
export function visibleObject(ctx: CommandContext, id: string): GameObject | Rejection {
  const object = findObject(ctx.draft, id);
  if (!object || !canSeeId(ctx.draft, object, ctx.actor)) return reject('unknownObject', id);
  return object;
}

/** The object, if the actor can see it and may act on it. */
export function actorObject(ctx: CommandContext, id: string): GameObject | Rejection {
  const object = visibleObject(ctx, id);
  if ('rejected' in object) return object;
  if (!mayActOn(object, ctx.actor)) return reject('notYours', id);
  return object;
}

/** Validates a whole-number count in `[min, max]`. */
export function checkCount(n: number, min: number, max: number, what: string): Rejection | undefined {
  if (!Number.isInteger(n) || n < min || n > max) return reject('outOfRange', `${what} must be ${min}-${max}`);
  return undefined;
}

/** Limits on command arguments (also enforced by the protocol schemas). */
export const LIMITS = {
  /** Cards per draw, channel, moveTop or lookAtTop. */
  cardsPerCommand: 60,
  /** Objects named by one exhaust/ready/reveal command. */
  objectsPerCommand: 60,
  tokensPerCommand: 10,
  /** Absolute delta of one counter / points / xp change. */
  counterDelta: 99,
  pointsDelta: 20,
  xpDelta: 99,
  /** Length of a custom counter or status name. */
  nameLength: 32,
} as const;
